import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Rating from '@mui/material/Rating';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import { DataTable, type DataTableColumn } from '@routes-plan/ui';
import PermissionButton from '../common/PermissionButton';
import RatingStars from './RatingStars';
import RateLoanDialog from './RateLoanDialog';
import { formatScore } from './ratingLevel';
import { formatCurrency } from '../../utils/format/currency';
import { useResourcePermissions } from '../../hooks/useResourcePermissions';
import { useT } from '../../i18n';

/**
 * Calificación del cliente en su hoja de vida: el total con su nivel, cómo se calcula y el detalle de cada
 * préstamo cerrado (cómo pagó y las estrellas). Quien edita clientes o registra abonos puede calificar o
 * corregir las estrellas.
 */
export interface ClientRatingItem {
  loanId: number;
  date: string | null;
  closedDate: string | null;
  value: number;
  auto: number | null;
  facts: { actualDays: number | null; termDays: number; maxGapDays: number | null; cloveDays: number | null } | null;
  stars: number | null;
  note: string | null;
  ratedBy: { id: number; username: string | null } | null;
  ratedFrom: 'web' | 'app' | null;
  score: number | null;
}

export interface ClientRatingData {
  score: number | null;
  level: string;
  loans: number;
  items: ClientRatingItem[];
  active: { loanId: number; daysLate: number; balance: number; date: string | null }[];
}

interface ClientRatingPanelProps {
  clientId: number | null | undefined;
  clientName?: string | null;
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  /** Avisa el total al cargar o al calificar (para mostrarlo en el encabezado de la hoja de vida). */
  onLoaded?: (data: ClientRatingData) => void;
}

const ClientRatingPanel: React.FC<ClientRatingPanelProps> = ({ clientId, clientName, showAlert, onLoaded }) => {
  const t = useT();
  const clientsPerm = useResourcePermissions('/admin/clients');
  const paymentsPerm = useResourcePermissions('/loans/payments');
  const canRate = clientsPerm.canUpdate || paymentsPerm.canPay;
  const [data, setData] = useState<ClientRatingData | null>(null);
  const [failed, setFailed] = useState(false);
  const [rating, setRating] = useState<ClientRatingItem | null>(null);

  const load = useCallback(async () => {
    if (!clientId) return;
    try {
      const { data: body } = await axios.get<ClientRatingData>(`clients/${clientId}/rating`);
      setData(body);
      setFailed(false);
      onLoaded?.(body);
    } catch {
      setFailed(true);
      showAlert(t('rating.loadError'), 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  useEffect(() => {
    setData(null);
    void load();
  }, [load]);

  const factsLine = (i: ClientRatingItem) => {
    const f = i.facts;
    if (!f) return '';
    const v = (n: number | null | undefined) => (n === null || n === undefined ? t('rating.factUnknown') : String(n));
    return t('rating.factsLine', { actual: v(f.actualDays), term: v(f.termDays), gap: v(f.maxGapDays), clove: v(f.cloveDays) });
  };

  const columns: DataTableColumn<ClientRatingItem>[] = useMemo(
    () => [
      { id: 'loan', header: t('rating.colLoan'), value: (i) => `#${i.loanId}`, sortable: true },
      { id: 'closed', header: t('rating.colClosed'), value: (i) => i.closedDate ?? '', sortable: true },
      { id: 'value', header: t('rating.colValue'), value: (i) => i.value, render: (i) => formatCurrency(i.value), align: 'right', sortable: true },
      {
        id: 'auto',
        header: t('rating.colAuto'),
        value: (i) => i.auto,
        render: (i) => (
          <Tooltip title={factsLine(i)}>
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
              {formatScore(i.auto)}
              <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            </Box>
          </Tooltip>
        ),
      },
      {
        id: 'stars',
        header: t('rating.colStars'),
        value: (i) => i.stars ?? '',
        render: (i) => (
          <Stack direction="row" spacing={0.5} alignItems="center">
            {i.stars ? (
              <Rating value={i.stars} readOnly size="small" />
            ) : (
              <Typography variant="caption" color="text.secondary">
                {t('rating.notRated')}
              </Typography>
            )}
            <PermissionButton
              allowed={canRate}
              deniedTooltip={t('rating.noPermission')}
              tooltip={i.stars ? t('rating.edit') : t('rating.rate')}
              variant={i.stars ? 'text' : 'outlined'}
              size="small"
              sx={{ minWidth: 32, px: 0.5 }}
              onClick={() => setRating(i)}
              aria-label={`${i.stars ? t('rating.edit') : t('rating.rate')} #${i.loanId}`}
            >
              <StarOutlineIcon fontSize="small" />
            </PermissionButton>
          </Stack>
        ),
      },
      { id: 'score', header: t('rating.colScore'), value: (i) => i.score, render: (i) => formatScore(i.score), align: 'center' },
      { id: 'note', header: t('rating.colNote'), value: (i) => i.note ?? '', wrap: true, hideBelow: 'sm' },
      {
        id: 'by',
        header: t('rating.colBy'),
        value: (i) => (i.ratedBy ? `${i.ratedBy.username ?? '—'}${i.ratedFrom ? ` · ${i.ratedFrom}` : ''}` : ''),
        hideBelow: 'sm',
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, canRate],
  );

  if (failed) return <Alert severity="error">{t('rating.loadError')}</Alert>;
  if (!data) return null;

  return (
    <Stack spacing={1} sx={{ minWidth: 0 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.5, sm: 2 }} alignItems={{ sm: 'center' }} flexWrap="wrap" useFlexGap>
        <Typography variant="subtitle2">{t('rating.title')}</Typography>
        <RatingStars score={data.score} />
        {data.loans > 0 && (
          <Typography variant="caption" color="text.secondary">
            {t('rating.loansCount', { count: data.loans })}
          </Typography>
        )}
        <Tooltip title={t('rating.how')} arrow enterTouchDelay={0}>
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'primary.main', cursor: 'help', typography: 'caption' }}>
            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
            {t('rating.howTitle')}
          </Box>
        </Tooltip>
      </Stack>
      {(data.active ?? []).map((a) => (
        <Typography key={a.loanId} variant="caption" color={a.daysLate > 0 ? 'warning.main' : 'text.secondary'}>
          {t('rating.active', { id: a.loanId, days: a.daysLate })}
        </Typography>
      ))}
      {(data.items ?? []).length > 0 ? (
        <DataTable
          rows={data.items}
          columns={columns}
          getRowId={(i) => i.loanId}
          maxHeight={320}
          initialPageSize={10}
          ariaLabel={t('rating.title')}
        />
      ) : (
        <Typography variant="body2" color="text.secondary">
          {t('rating.empty')}
        </Typography>
      )}
      <RateLoanDialog
        open={Boolean(rating)}
        loanId={rating?.loanId ?? null}
        clientName={clientName}
        initialStars={rating?.stars ?? null}
        initialNote={rating?.note ?? null}
        onClose={() => setRating(null)}
        onSaved={() => void load()}
        showAlert={showAlert}
      />
    </Stack>
  );
};

export default ClientRatingPanel;
